import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import { Trophy, Plus, Edit2, Trash2, AlertCircle, CheckCircle2 } from 'lucide-react';

interface RankForm {
  name: string;
  code: string;
  description: string;
  displayOrder: number;
  directBusinessRequired: number;
  teamBusinessRequired: number;
  rewardAmount: number;
  payoutDays: number;
  payoutType: string;
  isActive: boolean;
}

const INITIAL_FORM: RankForm = {
  name: '', code: '', description: '', displayOrder: 0,
  directBusinessRequired: 0, teamBusinessRequired: 0,
  rewardAmount: 0, payoutDays: 200, payoutType: 'WORKING_DAYS', isActive: true,
};

function RankFormModal({ isOpen, onClose, rank }: { isOpen: boolean; onClose: () => void; rank?: any }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<RankForm>(INITIAL_FORM);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const isEdit = !!rank;

  useEffect(() => {
    if (isOpen) {
      if (rank) {
        setForm({
          name: rank.name || '',
          code: rank.code || '',
          description: rank.description || '',
          displayOrder: rank.displayOrder || 0,
          directBusinessRequired: parseFloat(rank.directBusinessRequired) || 0,
          teamBusinessRequired: parseFloat(rank.teamBusinessRequired) || 0,
          rewardAmount: parseFloat(rank.rewardAmount) || 0,
          payoutDays: rank.payoutDays || 200,
          payoutType: rank.payoutType || 'WORKING_DAYS',
          isActive: rank.isActive !== false,
        });
      } else {
        setForm(INITIAL_FORM);
      }
      setError('');
      setSuccess(false);
    }
  }, [isOpen, rank]);

  const mutation = useMutation({
    mutationFn: (data: RankForm) => isEdit ? api.patch(`/bonanza/ranks/${rank.id}`, data) : api.post('/bonanza/ranks', data),
    onSuccess: () => { setSuccess(true); qc.invalidateQueries({ queryKey: ['bonanza-ranks'] }); },
    onError: (err: any) => setError(err.response?.data?.message || 'Failed to save rank'),
  });

  const set = (key: keyof RankForm, value: any) => setForm(prev => ({ ...prev, [key]: value }));
  const handleSubmit = () => {
    setError('');
    if (!form.name.trim()) { setError('Rank name is required'); return; }
    if (!form.code.trim()) { setError('Rank code is required'); return; }
    if (form.directBusinessRequired <= 0) { setError('Direct business must be > 0'); return; }
    if (form.teamBusinessRequired <= 0) { setError('Team business must be > 0'); return; }
    if (form.rewardAmount <= 0) { setError('Reward amount must be > 0'); return; }
    if (form.payoutDays <= 0) { setError('Payout days must be > 0'); return; }
    mutation.mutate(form);
  };
  const handleClose = () => { onClose(); };
  if (!isOpen) return null;
  const dailyAmount = form.payoutDays > 0 ? form.rewardAmount / form.payoutDays : 0;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={isEdit ? 'Edit Bonanza Rank' : 'Create Bonanza Rank'} maxWidth="max-w-2xl">
      {success ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
            <div>
              <p className="text-sm font-medium text-emerald-400">{isEdit ? 'Rank Updated' : 'Rank Created'}</p>
              <p className="text-xs text-slate-400 mt-0.5">Bonanza rank saved successfully</p>
            </div>
          </div>
          <Button onClick={handleClose} className="w-full">Done</Button>
        </div>
      ) : (
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Rank Name</label>
              <input type="text" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Ruby" className="w-full px-3 py-2 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-primary-500/50 transition-all" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Rank Code</label>
              <input type="text" value={form.code} onChange={e => set('code', e.target.value.toUpperCase())} placeholder="e.g. RUBY" className="w-full px-3 py-2 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-primary-500/50 transition-all" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} className="w-full px-3 py-2 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-primary-500/50 transition-all resize-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Display Order</label>
            <input type="number" value={form.displayOrder} onChange={e => set('displayOrder', parseInt(e.target.value) || 0)} className="w-full px-3 py-2 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white text-sm focus:outline-none focus:border-primary-500/50 transition-all" />
          </div>
          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] space-y-4">
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Business Requirements</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Direct Business Required</label>
                <input type="number" value={form.directBusinessRequired || ''} onChange={e => set('directBusinessRequired', parseFloat(e.target.value) || 0)} placeholder="100000" className="w-full px-3 py-2 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-primary-500/50 transition-all" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Team Business Required</label>
                <input type="number" value={form.teamBusinessRequired || ''} onChange={e => set('teamBusinessRequired', parseFloat(e.target.value) || 0)} placeholder="1000000" className="w-full px-3 py-2 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-primary-500/50 transition-all" />
              </div>
            </div>
          </div>
          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] space-y-4">
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Reward Configuration</p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Reward Amount</label>
                <input type="number" value={form.rewardAmount || ''} onChange={e => set('rewardAmount', parseFloat(e.target.value) || 0)} placeholder="25000" className="w-full px-3 py-2 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-primary-500/50 transition-all" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Payout Days</label>
                <input type="number" value={form.payoutDays || ''} onChange={e => set('payoutDays', parseInt(e.target.value) || 0)} placeholder="200" className="w-full px-3 py-2 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-primary-500/50 transition-all" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Payout Type</label>
                <select value={form.payoutType} onChange={e => set('payoutType', e.target.value)} className="w-full px-3 py-2 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white text-sm focus:outline-none focus:border-primary-500/50 transition-all">
                  <option value="WORKING_DAYS" className="bg-slate-800">Working Days</option>
                  <option value="CALENDAR_DAYS" className="bg-slate-800">Calendar Days</option>
                </select>
              </div>
            </div>
            {form.rewardAmount > 0 && form.payoutDays > 0 && (
              <div className="p-3 rounded-lg bg-primary-600/5 border border-primary-500/10">
                <p className="text-xs text-slate-400">Daily payout: <span className="text-primary-400 font-medium">{formatCurrency(dailyAmount)}</span> per working day</p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs font-medium text-slate-400">Status</label>
            <button type="button" onClick={() => set('isActive', !form.isActive)} className={`relative w-10 h-5 rounded-full transition-colors ${form.isActive ? 'bg-primary-600' : 'bg-white/10'}`}>
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${form.isActive ? 'translate-x-5' : ''}`} />
            </button>
            <span className="text-xs text-slate-400">{form.isActive ? 'Active' : 'Inactive'}</span>
          </div>
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
              <p className="text-xs text-red-400">{error}</p>
            </div>
          )}
          <div className="flex gap-3">
            <Button variant="secondary" onClick={handleClose} className="flex-1">Cancel</Button>
            <Button onClick={handleSubmit} isLoading={mutation.isPending} className="flex-1">{isEdit ? 'Update Rank' : 'Create Rank'}</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default function AdminBonanzaRanksPage() {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRank, setEditingRank] = useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<any>(null);

  const { data: ranks, isLoading } = useQuery({
    queryKey: ['bonanza-ranks'],
    queryFn: () => api.get('/bonanza/ranks').then(r => r.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/bonanza/ranks/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['bonanza-ranks'] }); setDeleteConfirm(null); },
  });

  const toggleActive = useMutation({
    mutationFn: (data: { id: string; isActive: boolean }) => api.patch(`/bonanza/ranks/${data.id}`, { isActive: data.isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bonanza-ranks'] }),
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Bonanza Ranks</h1>
          <p className="text-slate-400 mt-1">Configure achievement ranks and rewards</p>
        </div>
        <Button onClick={() => { setEditingRank(null); setModalOpen(true); }}><Plus className="w-4 h-4" />Add Rank</Button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-48 rounded-xl bg-white/[0.02] animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ranks?.map((rank: any) => (
            <div key={rank.id} className={`d-card-hover p-5 space-y-4 ${!rank.isActive ? 'opacity-50' : ''}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                    <Trophy className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{rank.name}</h3>
                    <p className="text-xs text-slate-500">{rank.code}</p>
                  </div>
                </div>
                <Badge status={rank.isActive ? 'ACTIVE' : 'INACTIVE'} />
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-slate-500 text-xs">Direct Business</p>
                  <p className="font-semibold text-white">{formatCurrency(rank.directBusinessRequired)}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Team Business</p>
                  <p className="font-semibold text-white">{formatCurrency(rank.teamBusinessRequired)}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Reward</p>
                  <p className="font-semibold text-emerald-400">{formatCurrency(rank.rewardAmount)}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Daily Payout</p>
                  <p className="font-semibold text-primary-400">{formatCurrency(rank.rewardAmount / rank.payoutDays)}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Payout Days</p>
                  <p className="font-semibold text-white">{rank.payoutDays}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Payout Type</p>
                  <p className="font-semibold text-white">{rank.payoutType === 'WORKING_DAYS' ? 'Working Days' : 'Calendar Days'}</p>
                </div>
              </div>

              <div className="flex gap-2 pt-2 border-t border-white/[0.06]">
                <button onClick={() => { setEditingRank(rank); setModalOpen(true); }} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white/[0.04] text-slate-400 text-xs hover:text-white hover:bg-white/[0.08] transition-colors">
                  <Edit2 className="w-3.5 h-3.5" />Edit
                </button>
                <button onClick={() => toggleActive.mutate({ id: rank.id, isActive: !rank.isActive })} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white/[0.04] text-slate-400 text-xs hover:text-white hover:bg-white/[0.08] transition-colors">
                  {rank.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button onClick={() => setDeleteConfirm(rank)} className="flex items-center justify-center px-3 py-2 rounded-lg bg-red-500/10 text-red-400 text-xs hover:bg-red-500/20 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {ranks?.length === 0 && (
        <div className="text-center py-12">
          <Trophy className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">No bonanza ranks configured yet</p>
          <Button onClick={() => { setEditingRank(null); setModalOpen(true); }} className="mt-4">Create First Rank</Button>
        </div>
      )}

      <RankFormModal isOpen={modalOpen} onClose={() => { setModalOpen(false); setEditingRank(null); }} rank={editingRank} />

      {deleteConfirm && (
        <Modal isOpen={true} onClose={() => setDeleteConfirm(null)} title="Delete Rank">
          <div className="space-y-4">
            <p className="text-sm text-slate-400">Are you sure you want to delete <span className="text-white font-medium">{deleteConfirm.name}</span>? This cannot be undone.</p>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)} className="flex-1">Cancel</Button>
              <Button variant="danger" onClick={() => deleteMutation.mutate(deleteConfirm.id)} isLoading={deleteMutation.isPending} className="flex-1">Delete</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
